<template>
  <div>
    <sc-render :fields="fields" :emptyMessage="$t('NO_DATA_SOURCE_EXIST')">
      <div
        :class="heroClasses"
        v-bind="accessibilityAttrs"
        @click.capture="handleTileLink"
        @keyup.enter="handleTileLink"
      >
        <div class="media-wrapper">
          <media-display
            :aspect-ratio="aspectRatio"
            :sc-image-field="desktopImageField"
            :image-mobile-source="mobileImageSrc"
            :image-desktop-source="desktopImageSrc"
            :video-source="videoUrl"
            :video-autoplay="autoplay"
            :video-loop="loop"
            :video-mute="mute || autoplay"
            :video-default-controls="!autoplay"
            :video-hidden-text="hiddenVideoTitle"
            :width="width"
            :height="height"
            @media-error="isVideoFailed = true"
          />
        </div>
        <HeroContent :fields="fields" ref="heroContent"/>
      </div>
    </sc-render>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { Image } from '@sitecore-jss/sitecore-jss-vue'
import get from '~/helpers/get'
import mapFields from '~/helpers/map-fields'
import MediaDisplay from '~/components/sitecore/media-display'
import ScRender from '~/components/sitecore/sc-render'
import HeroContent from './HeroContent'
import ThemeMixin from '~/mixins/theme'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

const defaultPosition = 'below'

const RATIO = {
  '16_9': '16:9',
  '5_3': '5:3',
  '9_16': '9:16',
  '3_4': '3:4'
}

export default {
  name: 'Hero',

  components: {
    HeroContent,
    ScImage: Image,
    ScRender,
    MediaDisplay
  },

  props: {
    fields: {
      type: Object,
      default: () => ({})
    },
    rendering: {
      type: Object
    }
  },

  mixins: [ThemeMixin],

  data () {
    return {
      isVideoFailed: false
    }
  },

  computed: {
    ...mapJssGetters(['isEditMode']),
    ...mapFields({
      textPosition: 'textPosition.value'
    }),

    isMobile () {
      return this.$device.isMobile
    },

    desktopVideoRatio () {
      return get(this.fields, 'mainVideoAspectRatio.fields.aspectRatio.value', RATIO['16_9'])
    },

    isPortraitView () {
      return this.desktopVideoRatio === RATIO['9_16']
    },

    aspectRatio () {
      switch (this.desktopVideoRatio) {
        case RATIO['16_9']: return '16/9'
        case RATIO['9_16']: return '9/16'
        default: return ''
      }
    },

    heroClasses () {
      return [
        'hero-content',
        { clickable: this.isClickable },
        { 'portrait-view': this.isPortraitView },
        { 'text-bottom': this.getTextPosition !== defaultPosition }
      ]
    },

    videoUrl () {
      return this.showMobileVideo ? this.mobileVideoUrl : this.desktopVideoUrl
    },

    isVideo () {
      return !this.isVideoFailed && this.videoUrl
    },

    showMobileVideo () {
      return this.isMobile && this.autoplay && this.mobileVideoUrl
    },

    mobileImage () {
      return get(this.fields, 'mobileImage.value', {})
    },

    mobileImageSrc () {
      // use the desktop portrait image on mobile when the mobile image is not set
      if (!this.mobileImage.src && this.isPortraitView) return this.desktopImage.src

      // use the desktop image on mobile when the desktop video is only set
      if (this.desktopVideoUrl && !this.mobileVideoUrl) return this.desktopImage.src

      return this.mobileImage.src || this.desktopImage.cloudinary_3x4_fill_crop
    },

    desktopVideoUrl () {
      return get(this.fields, 'mainVideo.value', '')
    },

    mobileVideoUrl () {
      return get(this.fields, 'mobileVideo.value', '')
    },

    autoplay () {
      return get(this.fields, 'autoplay.value', false)
    },

    loop () {
      return get(this.fields, 'loop.value', false)
    },

    mute () {
      return get(this.fields, 'mute.value', false)
    },

    hiddenVideoTitle () {
      return get(this.fields, 'hiddenVideoTitle.value', '')
    },

    desktopImageField () {
      return get(this.fields, 'mainImage', {})
    },

    desktopImage () {
      return get(this.fields, 'mainImage.value', {})
    },

    width () {
      return this.desktopImage.width ? Number(this.desktopImage.width) : this.desktopImage.width
    },

    height () {
      return this.desktopImage.height ? Number(this.desktopImage.height) : this.desktopImage.height
    },

    desktopImageSrc () {
      return get(this.desktopImage, 'src')
    },

    cta1 () {
      return get(this.fields, 'cta1.value', {})
    },

    cta2 () {
      return get(this.fields, 'cta2.value', {})
    },

    getTextPosition () {
      return (this.textPosition && this.textPosition.split(' ')[1]) || defaultPosition
    },

    isClickable () {
      return (this.cta2.href || this.cta1.href)
        && !(this.cta2.href && this.cta1.href)
        && !this.videoUrl
        && !this.isEditMode
    },

    accessibilityAttrs () {
      if (this.videoUrl) {
        return { tabindex: '0' }
      }

      return this.isClickable ? { role: 'link', tabindex: '0' } : {}
    }
  },

  methods: {
    handleTileLink () {
      if (!this.isClickable) return
      this.$refs.heroContent.contentAction()
    }
  }
}
</script>

<style lang="scss" scoped>

@mixin black-border {
  border-top: $space-060 solid $color-black;
  border-bottom: $space-060 solid $color-black;
}

.hero-content {
  position: relative;
  margin-bottom: $space-090;

  @include tablet() {
    margin-bottom: $space-100;
  }

  &.clickable {
    cursor: pointer;
  }

  &.portrait-view {
    .media-wrapper {
      @include tablet {
        text-align: center;
        background-color: $color-black;
        @include black-border;
      }
    }

    &.text-bottom {
      @include tablet {
        @include black-border;
      }

      .media-wrapper {
        @include tablet {
          border: none;
          @include ratio(5, 3);
        }
      }
    }
  }
}

</style>
